import React, { useState, useEffect } from 'react';
import BlasterHighScoreService from '../utils/blasterHighScoreService';
import { ensureUserUUID, USER_UUID_KEY } from '../utils/uuidUtils';
import supabaseDAL from '../utils/supabaseDAL';
import { ENVIRONMENT, getSupabaseConfig } from '../utils/supabaseClient';
import { BAGPIPE_USER_INITIALS_KEY } from '../utils/constants';
import './BlasterHighScores.css';

// Constants
// No need to redefine USER_INITIALS_KEY, now using BAGPIPE_USER_INITIALS_KEY

// Migrate old local user ID to new UUID system
const migrateLocalUserId = () => {
  const oldLocalUserId = localStorage.getItem('localUserId');
  if (oldLocalUserId && oldLocalUserId.startsWith('local-')) {
    const uuid = ensureUserUUID();
    // Note: We're not deleting the old ID yet, in case we need it for debugging
    localStorage.setItem('old_localUserId', oldLocalUserId);
    localStorage.removeItem('localUserId');
    return uuid;
  }
  return ensureUserUUID();
};

// Use the proper UUID system for local user ID
const getLocalUserId = () => {
  const uuid = migrateLocalUserId();
  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(uuid)) {
    console.error('Invalid UUID format:', uuid);
    // Generate a new one if invalid
    localStorage.removeItem(USER_UUID_KEY);
    return ensureUserUUID();
  }
  return uuid;
};

const LeaderBoard = () => {
  const [highScores, setHighScores] = useState([]);
  const [userScores, setUserScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newScore, setNewScore] = useState({ 
    level: 1, 
    score: 0,
    struserinitials: localStorage.getItem(BAGPIPE_USER_INITIALS_KEY) || ''
  });
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [success, setSuccess] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Idle');
  const [environment, setEnvironment] = useState(supabaseDAL.currentEnvironment);
  const [hasEnvVars, setHasEnvVars] = useState(true);
  const localUserId = getLocalUserId();

  // Get the local high score, Level, and BlasterLevel from localStorage
  const localHighScore = localStorage.getItem('blaster_high_score');
  const localLevel = localStorage.getItem('Level');
  const localBlasterLevel = localStorage.getItem('BlasterLevel');

  // Fetch high scores on component mount
  useEffect(() => {
    fetchHighScores();
    fetchUserScores();
  }, []);

  // Remove old blaster_user_initials key on load
  useEffect(() => {
    if (localStorage.getItem('blaster_user_initials')) {
      localStorage.removeItem('blaster_user_initials');
    }
  }, []);

  // Fetch high scores with optional level filter
  const fetchHighScores = async () => {
    setLoading(true);
    try {
      let options = {
        limit: 10,
        orderBy: 'score',
        ascending: false
      };

      // Apply level filter if selected
      if (selectedLevel !== 'all') {
        options.filter = { level: parseInt(selectedLevel, 10) };
      }

      const { data, error } = await BlasterHighScoreService.getHighScores(options);
      if (error) throw error;
      setHighScores(data || []);
    } catch (err) {
      setError('Failed to load high scores');
    } finally {
      setLoading(false);
    }
  };

  // Fetch scores for the current user
  const fetchUserScores = async () => {
    try {
      const { data, error } = await BlasterHighScoreService.getUserHighScores(null, localUserId);
      if (error) throw error;
      setUserScores(data || []);
    } catch (err) {
      // Don't set global error for user scores to avoid UI disruption
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'struserinitials') {
      // Limit to 3 characters and convert to uppercase
      processedValue = value.slice(0, 3).toUpperCase();
      // Save to localStorage
      localStorage.setItem(BAGPIPE_USER_INITIALS_KEY, processedValue);
    } else if (name === 'level' || name === 'score') {
      processedValue = parseInt(value, 10);
    }

    setNewScore(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  // Submit a new high score
  const handleSubmitScore = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    
    try {
      const scoreData = {
        level: newScore.level,
        score: newScore.score,
        local_user_id: localUserId,
        struserinitials: newScore.struserinitials
      };
      
      const { data, error } = await BlasterHighScoreService.addHighScore(scoreData);
      
      if (error) throw error;
      
      setSuccess('Score submitted successfully!');
      
      // Refresh the high scores lists
      fetchHighScores();
      fetchUserScores();
    } catch (err) {
      setError(err.message || 'Failed to submit score');
    } finally {
      setLoading(false);
    }
  };

  // Handle level filter change
  const handleLevelChange = (e) => {
    setSelectedLevel(e.target.value);
  };

  // Test the database connection
  const handleTestConnection = async () => {
    try {
      setConnectionStatus('Testing connection...');
      const response = await supabaseDAL.testConnection();
      
      if (response.success) {
        setConnectionStatus('Connected successfully');
        setError(null);
      } else {
        setConnectionStatus('Connection failed');
        setError(response.error);
      }
    } catch (err) {
      setConnectionStatus('Error occurred');
      setError(err.message);
    }
  };

  // Switch environments
  const handleSwitchEnvironment = (env) => {
    try {
      setConnectionStatus(`Switching to ${env} environment...`);
      const success = supabaseDAL.switchEnvironment(env);
      
      if (success) {
        setEnvironment(env);
        
        // Check if the new environment has valid config
        const newConfig = getSupabaseConfig(env);
        const configValid = newConfig.URL && newConfig.ANON_KEY;
        setHasEnvVars(configValid);
        
        setConnectionStatus(configValid ? 
          `Switched to ${env} environment` : 
          `Switched to ${env} environment (missing environment variables)`);

        // Clear any existing errors
        setError(null);
      } else {
        setConnectionStatus(`Failed to switch to ${env} environment`);
        setError('Environment switch failed');
      }
    } catch (err) {
      setConnectionStatus(`Error switching to ${env} environment`);
      setError(err.message);
    }
  };

  // Check environment variables on mount and when environment changes
  useEffect(() => {
    const currentConfig = getSupabaseConfig(environment);
    const configValid = currentConfig.URL && currentConfig.ANON_KEY;
    setHasEnvVars(configValid);
    
    if (!configValid) {
      setConnectionStatus('Missing environment variables');
      setError('Database configuration is incomplete');
    }
  }, [environment]);

  // Apply the level filter
  useEffect(() => {
    fetchHighScores();
  }, [selectedLevel]);

  return (
    <div className="blaster-high-scores">
      <h2>Leader Board</h2>

      {/* Your High Score section */}
      <div className="local-high-score-section" style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1.1rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            You:
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffd700' }}>
              {localHighScore !== null ? localHighScore : 'N/A'}
            </span>
          </span>
          <span style={{ fontSize: '1.1rem', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            BlasterLevel:
            <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ffd700' }}>
              {localBlasterLevel !== null ? localBlasterLevel : 'N/A'}
            </span>
          </span>
        </div>
      </div>

      {/* High scores display */}
      <div className="high-scores-section">
        <div className="section-header">
          <h3>Global High Scores</h3>
          
          <div className="level-filter">
            <label htmlFor="level-filter">Filter by Level:</label>
            <select 
              id="level-filter" 
              value={selectedLevel} 
              onChange={handleLevelChange}
            >
              <option value="all">All Levels</option>
              {[1, 2, 3, 4, 5].map(level => (
                <option key={level} value={level}>Level {level}</option>
              ))}
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading high scores...</div>
        ) : highScores.length > 0 ? (
          <table className="scores-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Level</th>
                <th>Score</th>
                <th>Initials</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {highScores.map((score, index) => (
                <tr key={score.id} className={score.local_user_id === localUserId ? 'highlight-row' : ''}>
                  <td>{index + 1}</td>
                  <td>{score.level}</td>
                  <td>{score.score}</td>
                  <td>{score.struserinitials || score.struserInitials || score.initials || '---'}</td>
                  <td>{new Date(score.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-scores">No high scores found</div>
        )}
      </div>
      
      {/* User's personal scores */}
      <div className="user-scores-section">
        <h3>Your High Scores</h3>
        
        {userScores.length > 0 ? (
          <table className="scores-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Score</th>
                <th>Initials</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {userScores.map(score => (
                <tr key={score.id}>
                  <td>{score.level}</td>
                  <td>{score.score}</td>
                  <td>{score.struserinitials || score.struserInitials || score.initials || '---'}</td>
                  <td>{new Date(score.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-scores">You haven't submitted any scores yet</div>
        )}
      </div>
    </div>
  );
};

export default LeaderBoard; 